import { useQuery } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { z } from 'zod';
import { useEnabledQuery } from '../../../../../ui/hooks/useEnabledQuery';
import { getMutationSchema } from '../../../../../ui/util/getMutationSchema';
import { ResourceType } from '../../../../enumerations/ResourceType';
import {
    StructuredSystemAnnouncementsOutlineSchema,
    SystemAnnouncementOutlineSchema,
} from '../../../../outlines/systemAnnouncementOutline';
import { get } from '../../../../resource-base/APIVersion1';
import { parseRawData, useOnUnauthorized } from '../../../../resource-base/adapters/AdapterUtils';
import { useMultiParentlessResourceAdapterActions } from '../../../../resource-base/adapters/useMultiParentlessResourceAdapterActions';
import {
    defaultPersistableResource,
    PersistableResourceSchema,
} from '../../../../resource-base/persistableResourceTypes';

export enum SystemSystemAnnouncementPropertyValidation {
    TITLE = 'TITLE',
    TEXT = 'TEXT',
    ACTIVE_FROM = 'ACTIVE_FROM',
    ACTIVE_UNTIL = 'ACTIVE_UNTIL',
}

export const SystemAnnouncementResourceSchema = PersistableResourceSchema.extend({
    title: z.string(),
    text: z.string(),
    activeFrom: z.string(),
    activeUntil: z.string(),
    resourceType: z.literal(ResourceType.SYSTEM_ANNOUNCEMENT).default(ResourceType.SYSTEM_ANNOUNCEMENT),
});

export const SystemAnnouncementMutationResourceSchema = getMutationSchema(SystemAnnouncementResourceSchema);

export type SystemAnnouncementResource = z.infer<typeof SystemAnnouncementResourceSchema>;

export const defaultSystemAnnouncementResource: SystemAnnouncementResource = {
    ...defaultPersistableResource,
    resourceType: ResourceType.SYSTEM_ANNOUNCEMENT,
    title: '',
    text: '',
    activeFrom: dayjs().add(1, 'day').toISOString(),
    activeUntil: dayjs().add(2, 'day').toISOString(),
};

export function useSystemAnnouncementActions() {
    const getEnabled = useEnabledQuery();
    const onUnauthorized = useOnUnauthorized();
    const actions = useMultiParentlessResourceAdapterActions(
        'system/announcement',
        ResourceType.SYSTEM_ANNOUNCEMENT,
        SystemAnnouncementResourceSchema
    );

    const useGetAllActiveSystemAnnouncements = (userId: string | undefined | null) => {
        const userParam = userId ? `?user=${userId}` : '';
        return useQuery({
            queryKey: [userId, ResourceType.SYSTEM_ANNOUNCEMENT],
            queryFn: () =>
                get(`system/announcement/all-active${userParam}`).then((result) => {
                    if (result.status === 200) {
                        return result
                            .json()
                            .then((outline) => parseRawData(outline, SystemAnnouncementOutlineSchema.array()));
                    } else if (result.status === 401) {
                        return onUnauthorized(result, null).then((_) => {
                            throw Error('Unauthorized!');
                        });
                    }
                }),

            enabled: getEnabled(userId),
            refetchInterval: 600000,
            staleTime: 0,
        });
    };

    const useGetStructuredSystemAnnouncement = () => {
        return useQuery({
            queryKey: ['structure', ResourceType.SYSTEM_ANNOUNCEMENT],
            queryFn: () =>
                get(`system/announcement/all-structured`).then((result) => {
                    if (result.status === 200) {
                        return result
                            .json()
                            .then((outline) => parseRawData(outline, StructuredSystemAnnouncementsOutlineSchema));
                    } else if (result.status === 401) {
                        return onUnauthorized(result, null).then((_) => {
                            throw Error('Unauthorized!');
                        });
                    }
                }),
            enabled: getEnabled(true),
        });
    };

    return {
        ...actions,
        useGetAllActiveSystemAnnouncements,
        useGetStructuredSystemAnnouncement,
    };
}
